import { useRef } from 'react';
import MDEditor, { commands, type ICommand } from '@uiw/react-md-editor';
import '@uiw/react-md-editor/markdown-editor.css';
import '@uiw/react-markdown-preview/markdown.css';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import { useEditorImageUpload } from '@/hooks/useEditorImageUpload';

interface RichMdEditorProps {
  value: string;
  onChange: (val: string) => void;
  height?: number;
  placeholder?: string;
  lang?: 'zh' | 'en' | 'ru';
}

export default function RichMdEditor({
  value,
  onChange,
  height = 500,
  placeholder = '在此输入正文内容，支持 Markdown 格式...',
}: RichMdEditorProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const apiRef = useRef<any>(null);
  const { uploading, uploadFile } = useEditorImageUpload();

  const imageUploadCommand: ICommand = {
    name: 'imageUpload',
    keyCommand: 'imageUpload',
    buttonProps: {
      'aria-label': '上传图片',
      title: '上传图片插入正文',
    },
    icon: (
      <span style={{ lineHeight: 1 }}>
        <i className="ri-image-add-line" style={{ fontSize: 13 }}></i>
      </span>
    ),
    execute: (_state, api) => {
      apiRef.current = api;
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    },
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    const result = await uploadFile(file, 'knowledge');
    if (result.url && apiRef.current) {
      apiRef.current.replaceSelection(`![图片](${result.url})\n`);
    } else if (result.error) {
      // eslint-disable-next-line no-alert
      alert(`图片上传失败：${result.error}`);
    }
  };

  return (
    <div data-color-mode="light" className="relative">
      {uploading && (
        <div className="absolute inset-0 z-10 bg-white/70 flex items-center justify-center rounded-xl">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <i className="ri-loader-4-line animate-spin text-xl text-[#c9a96e]"></i>
            图片上传中，请稍候...
          </div>
        </div>
      )}

      <MDEditor
        value={value}
        onChange={(val) => onChange(val ?? '')}
        height={height}
        visibleDragbar={false}
        preview="live"
        previewOptions={{
          rehypePlugins: [
            [
              rehypeSanitize,
              {
                ...defaultSchema,
                attributes: {
                  ...defaultSchema.attributes,
                  img: ['src', 'alt', 'title', 'width', 'height', 'className', 'style'],
                },
              },
            ],
          ],
        }}
        commands={[
          commands.bold,
          commands.italic,
          commands.strikethrough,
          commands.divider,
          commands.title1,
          commands.title2,
          commands.title3,
          commands.divider,
          commands.unorderedListCommand,
          commands.orderedListCommand,
          commands.checkedListCommand,
          commands.divider,
          commands.quote,
          commands.hr,
          commands.code,
          commands.codeBlock,
          commands.divider,
          commands.link,
          imageUploadCommand,
          commands.table,
          commands.divider,
          commands.codeEdit,
          commands.codeLive,
          commands.codePreview,
        ]}
        extraCommands={[commands.fullscreen]}
        textareaProps={{ placeholder }}
      />

      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={handleFileChange}
      />

      <p className="text-xs text-gray-400 mt-1.5 flex items-center gap-1.5">
        <i className="ri-image-line"></i>
        点击工具栏图片图标可上传图片插入正文（最大 5MB，JPG / PNG / WebP / GIF）
      </p>
    </div>
  );
}